import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { copyToClipboardWithConfirmation } from '@openx/utils/lib/copyToClipboard.ts';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import type { AppNameSuggestion, InvalidAppNameWithSuggestions } from '../../types.ts';

const containerSx: SxProps<Theme> = {
  pb: 2,
};

const sectionTitleSx: SxProps<Theme> = {
  color: theme => theme.palette.error.main,
  fontWeight: 'fontWeightRegular',
  mb: 1,
};

const invalidItemsContainerSx: SxProps<Theme> = {
  border: theme => `1px solid ${theme.palette.error.main}`,
  maxHeight: 300,
  overflowY: 'auto',
  p: theme => theme.spacing(1, 1.5),
  mb: 1.5,
  backgroundColor: theme => theme.palette.background.boxLight,
};

const invalidNameRowSx: SxProps<Theme> = {
  mb: 1.5,
  '&:last-child': {
    mb: 0,
  },
};

const invalidNameSx: SxProps<Theme> = {
  fontWeight: 'fontWeightBold',
  color: 'text.primary',
  mb: 0.5,
};

const getSuggestionRowSx = (isAlreadyAdded?: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  py: 0.25,
  px: 0,
  color: isAlreadyAdded ? 'text.disabled' : 'text.primary',
});

const getSuggestionNameSx = (isAlreadyAdded?: boolean): SxProps<Theme> => ({
  color: isAlreadyAdded ? 'text.disabled' : 'text.primary',
});

const addButtonSx: SxProps<Theme> = {
  p: 0.25,
  '&:hover': {
    backgroundColor: 'action.hover',
  },
};

type AppNameSuggestionsV2Props = {
  invalidItemsWithSuggestions: InvalidAppNameWithSuggestions[];
  onAddSuggestion: (suggestion: AppNameSuggestion, invalidName: string) => void;
  errorText?: string;
};

// Displays invalid App Names with backend-driven suggestions (appGroupSearch).
export function AppNameSuggestionsV2({
  invalidItemsWithSuggestions,
  onAddSuggestion,
  errorText,
}: AppNameSuggestionsV2Props) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const invalidNamesText = invalidItemsWithSuggestions.map(item => item.invalidName).join('\n');

  const handleAddSuggestion = (suggestion: AppNameSuggestion, invalidName: string) => {
    if (!suggestion.isAlreadyAdded) {
      onAddSuggestion(suggestion, invalidName);
    }
  };

  return (
    <Box sx={containerSx} data-test="app-name-suggestions-v2">
      {errorText && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {errorText}
        </Typography>
      )}

      <Typography variant="caption" sx={sectionTitleSx} data-test="suggestions-section-title">
        {t('Invalid App Names & Suggested Alternatives')}
      </Typography>

      <Box sx={invalidItemsContainerSx} data-test="invalid-items-with-suggestions">
        {invalidItemsWithSuggestions.map(({ invalidName, suggestions }) => (
          <Box key={invalidName} sx={invalidNameRowSx} data-test="invalid-name-row">
            <Typography variant="body2" sx={invalidNameSx} data-test="invalid-name">
              {invalidName}
            </Typography>

            {suggestions.length > 0 ? (
              suggestions.map(suggestion => (
                <Box key={suggestion.id} sx={getSuggestionRowSx(suggestion.isAlreadyAdded)} data-test="suggestion-row">
                  <Typography
                    variant="body2"
                    sx={getSuggestionNameSx(suggestion.isAlreadyAdded)}
                    data-test="suggestion-name"
                  >
                    {suggestion.name}
                  </Typography>

                  {!suggestion.isAlreadyAdded && (
                    <IconButton
                      size="small"
                      sx={addButtonSx}
                      onClick={() => handleAddSuggestion(suggestion, invalidName)}
                      data-test="add-suggestion-button"
                      aria-label={t('Add {name}', { name: suggestion.name })}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))
            ) : (
              <Typography
                variant="body2"
                data-test="no-suggestions"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('No similar names found')}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <Button
        type="button"
        onClick={() => copyToClipboardWithConfirmation(invalidNamesText, enqueueSnackbar, t)}
        data-test="copy-invalid-names-button"
      >
        {t('Copy to Clipboard')}
      </Button>
    </Box>
  );
}
